import React from 'react';
import type { Icon } from '@phosphor-icons/react';

export interface Segment<K extends string> {
  key: K;
  label: string;
  icon?: Icon;
  /** Zahl hinter dem Label (z. B. Anzahl sichtbarer Widgets). */
  anzahl?: number;
}

export interface SegmentedControlProps<K extends string> {
  segmente: readonly Segment<K>[];
  wert: K;
  onChange: (k: K) => void;
  /**
   * weich: aktives Segment hebt sich mit --bg + Schatten ab (Werkzeugleiste, Formulare).
   * akzent: aktives Segment in --acc-fill (Filter in Panels, z. B. Mahnwesen „Aktiv“).
   */
  art?: 'weich' | 'akzent';
  /** Höhe der Segmente: 30 (klein), 34 (Werkzeugleiste), 36 (Formular), 44 (mobil, Hit-Target). */
  hoehe?: 30 | 34 | 36 | 44;
  /** Segmente teilen sich die volle Breite. */
  voll?: boolean;
  ariaLabel?: string;
  className?: string;
}

export function SegmentedControl<K extends string>({
  segmente, wert, onChange, art = 'weich', hoehe = 34, voll = false, ariaLabel, className = '',
}: SegmentedControlProps<K>) {
  const radiusAussen = hoehe === 30 ? 13 : hoehe === 34 ? 16 : hoehe === 44 ? 20 : 14;
  const radiusInnen = hoehe === 30 ? 10 : hoehe === 34 ? 13 : hoehe === 44 ? 17 : 11;
  const schrift = hoehe === 30 ? 12 : 13;

  const tastatur = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = segmente.findIndex(s => s.key === wert);
    const n = segmente.length;
    const naechster = segmente[(i + (e.key === 'ArrowRight' ? 1 : n - 1)) % n];
    if (naechster) onChange(naechster.key);
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={tastatur}
      className={`${voll ? 'flex' : 'inline-flex'} flex-wrap ${className}`}
      style={{ padding: 3, borderRadius: radiusAussen, background: 'var(--fill2)' }}
    >
      {segmente.map(s => {
        const an = s.key === wert;
        const IconKomp = s.icon;
        const aktivStil: React.CSSProperties = art === 'akzent'
          ? { background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }
          : { background: 'var(--bg)', color: 'var(--ink)', boxShadow: '0 2px 8px -2px var(--shade)' };
        return (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={an}
            tabIndex={an ? 0 : -1}
            onClick={() => onChange(s.key)}
            className="inline-flex items-center justify-center whitespace-nowrap cursor-pointer"
            style={{
              flex: voll ? 1 : undefined,
              height: hoehe,
              padding: '0 13px',
              gap: 6,
              border: 'none',
              borderRadius: radiusInnen,
              fontSize: schrift,
              fontWeight: 700,
              transition: 'all .2s',
              ...(an ? aktivStil : { background: 'transparent', color: 'var(--ink2)', boxShadow: 'none' }),
            }}
          >
            {IconKomp && <IconKomp weight="bold" size={schrift} />}
            {s.label}
            {s.anzahl !== undefined && (
              <span className="immo-zahl" style={{ fontSize: 11, fontWeight: 800, opacity: 0.55 }}>{s.anzahl}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
